/* eslint-disable no-magic-numbers */

import { SOCIAL_AD_TYPES } from "utils/constants";
import { GIGA, KILO, MEGA, SECONDS_PER_MIN } from "utils/common";

import { isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import { filesize, duration, aspectRatios } from "widgets/forms/validators/Assets";
import {
	requiredAsset,
	validateUnlessExternalRender,
	errorMessageReducer,
	makeDynamicConfig,
	minCards,
	cardValidator,
	conditionalRequiredNotNull, requiredImposiumComp, requiredImposiumProject,
	requiredAfterEffectsPackage, requiredAfterEffectsComp
} from "./validators";
import {
	mergeLists,
	uniqueVariableName,
	uniqueCardVariableName
} from "social/validators/utils";
import { imageMaxBytes, videoMaxBytes } from "../utils";

/**
 * Pinterest Image
 ********************************************/

export const PINTEREST_COMMON_CONSTRAINTS = {
	MAX_DISCLOSURE_URL_LENGTH: 800,
};

export const PINTEREST_IMAGE_CONSTRAINTS = {
	MAX_LENGTH_TITLE: 100,
	MAX_LENGTH_DESCRIPTION: 500,
	MAX_LENGTH_ALT_TEXT: 500,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_IMAGE_BYTES: imageMaxBytes(10 * MEGA),
	IMAGE_ACCEPT: ".jpeg, .jpg, .png",
};

export const PINTEREST_IMAGE_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	title: [
		maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_TITLE),
	],
	description: [
		maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_DESCRIPTION),
	],
	altText: [
		maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_ALT_TEXT),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	image: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: PINTEREST_IMAGE_CONSTRAINTS.MAX_IMAGE_BYTES,
		}),
	]),
	disclosureType: [
		conditionalRequiredNotNull("disclosureEnabled"),
	],
	disclosureUrl: [
		conditionalRequiredNotNull("disclosureEnabled"),
		isURL,
		maxLength(PINTEREST_COMMON_CONSTRAINTS.MAX_DISCLOSURE_URL_LENGTH),
	],
};

const PINTEREST_IMAGE_WARNINGS = mergeLists(
	{
		image: [uniqueVariableName],
		title: [uniqueVariableName],
		description: [uniqueVariableName],
		landingPage: [uniqueVariableName],
	},
	makeDynamicConfig(PINTEREST_IMAGE_VALIDATORS)
);
export const PINTEREST_NO_DISCLOSURE = "NO_DISCLOSURE";
export const PINTEREST_DISCLOSURE_TYPES = {
	PRESCRIBING_INFORMATION: "Prescribing information",
	PRESCRIBING_INFORMATION_BOX_WARNING: "Prescribing information box warning",
	IMPORTANT_SAFETY_INFO: "Important safety info",
	MED_GUIDE: "Med guide",
	PATIENT_INFORMATION: "Patient information",
};

export function pinterestImageWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_IMAGE;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(PINTEREST_IMAGE_WARNINGS, values, formName);
	}
}

export function pinterestImageValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_IMAGE;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...PINTEREST_IMAGE_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

/**
 * Pinterest Video
 ********************************************/

export const PINTEREST_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_TITLE: 100,
	MAX_LENGTH_DESCRIPTION: 500,
	MAX_LENGTH_ALT_TEXT: 500,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_VIDEO_BYTES: videoMaxBytes(2 * GIGA),
	MIN_VIDEO_SEC: 4,
	MAX_VIDEO_MIN: 15,
	VIDEO_ACCEPT: ".mp4, .mov",

	THUMBNAIL_ACCEPT: ".jpg, .jpeg, .png",
	MAX_THUMBNAIL_BYTES: imageMaxBytes(500 * KILO),
};

export const PINTEREST_VIDEO_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	afterEffectsPackage: [
		requiredAfterEffectsPackage,
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp,
	],
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({ max: PINTEREST_VIDEO_CONSTRAINTS.MAX_VIDEO_BYTES }),
		duration({
			min: PINTEREST_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
			max: PINTEREST_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
	]),
	thumbnail: validateUnlessExternalRender([
		filesize({
			max: PINTEREST_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_BYTES,
		}),
	]),
	title: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_TITLE),
	],
	description: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_DESCRIPTION),
	],
	altText: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_ALT_TEXT),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	disclosureType: [
		conditionalRequiredNotNull("disclosureEnabled"),
	],
	disclosureUrl: [
		conditionalRequiredNotNull("disclosureEnabled"),
		isURL,
		maxLength(PINTEREST_COMMON_CONSTRAINTS.MAX_DISCLOSURE_URL_LENGTH),
	],
};

const PINTEREST_VIDEO_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		thumbnail: [uniqueVariableName],
		title: [uniqueVariableName],
		description: [uniqueVariableName],
		landingPage: [uniqueVariableName],
	},
	makeDynamicConfig(PINTEREST_VIDEO_VALIDATORS)
);

export function pinterestVideoWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_VIDEO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(PINTEREST_VIDEO_WARNINGS, values, formName);
	}
}

export function pinterestVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_VIDEO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...PINTEREST_VIDEO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}


/**
 * Pinterest Max Width Video
 ********************************************/

export const PINTEREST_MAX_WIDTH_VIDEO_VALIDATORS = {
	afterEffectsPackage: [
		requiredAfterEffectsPackage
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp
	],
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: PINTEREST_VIDEO_CONSTRAINTS.MAX_VIDEO_BYTES,
		}),
		duration({
			min: PINTEREST_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
			max: PINTEREST_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
		aspectRatios([
			{ width: 1, height: 1 },
			{ width: 16, height: 9 },
		]),
	]),
	thumbnail: validateUnlessExternalRender([
		filesize({
			max: PINTEREST_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_BYTES,
		}),
	]),
	title: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_TITLE),
	],
	description: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_DESCRIPTION),
	],
	altText: [
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_ALT_TEXT),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(PINTEREST_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	disclosureType: [
		conditionalRequiredNotNull("disclosureEnabled"),
	],
	disclosureUrl: [
		conditionalRequiredNotNull("disclosureEnabled"),
		isURL,
		maxLength(PINTEREST_COMMON_CONSTRAINTS.MAX_DISCLOSURE_URL_LENGTH),
	],
};

const PINTEREST_MAX_WIDTH_VIDEO_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		thumbnail: [uniqueVariableName],
		title: [uniqueVariableName],
		description: [uniqueVariableName],
		landingPage: [uniqueVariableName],
	},
	makeDynamicConfig(PINTEREST_MAX_WIDTH_VIDEO_VALIDATORS)
);

export function pinterestMaxWidthVideoWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(PINTEREST_MAX_WIDTH_VIDEO_WARNINGS, values, formName);
	}
}

export function pinterestMaxWidthVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...PINTEREST_MAX_WIDTH_VIDEO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

/**
 * Pinterest Carousel
 ********************************************/
export const PINTEREST_CAROUSEL_CARD_VALIDATORS = {
	image: validateUnlessExternalRender([requiredAsset]),
	title: [maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_TITLE)],
	description: [maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_DESCRIPTION)],
	landingPage: [requiredNotNull, isURL, maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE)],
};

const PINTEREST_CAROUSEL_CARD_WARNINGS = mergeLists(
	{
		image: [uniqueCardVariableName],
		title: [uniqueCardVariableName],
		description: [uniqueCardVariableName],
		landingPage: [uniqueCardVariableName],
	},
	makeDynamicConfig({
		...PINTEREST_CAROUSEL_CARD_VALIDATORS,
	})
);

export const PINTEREST_CAROUSEL_VALIDATORS = {
	altText: [maxLength(PINTEREST_IMAGE_CONSTRAINTS.MAX_LENGTH_ALT_TEXT)],
	cards: [cardValidator(PINTEREST_CAROUSEL_CARD_WARNINGS)],
	cardCount: [minCards(SOCIAL_AD_TYPES.PINTEREST_CAROUSEL)],
};

const PINTEREST_CAROUSEL_WARNINGS = {
	...PINTEREST_CAROUSEL_VALIDATORS,
};

export function pinterestCarouselWarning(allValues, props){
	const formName = SOCIAL_AD_TYPES.PINTEREST_CAROUSEL;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(PINTEREST_CAROUSEL_WARNINGS, values, formName);
	}
}

export function pinterestCarouselValidator(allValues, props){
	const formName = SOCIAL_AD_TYPES.PINTEREST_CAROUSEL;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...PINTEREST_CAROUSEL_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
